import { useCallback, useEffect, useState } from 'react';
import { AssetModelTab } from './index';
import { AssetModel, AssetModelForm, MaintenanceConfig, PayloadAssetModels } from '../../../type';
import {
  createNewAssetModel,
  deleteAssetModel,
  deleteFileModel,
  updateNewAssetModel,
  uploadFileAssetModel
} from 'services/asset';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { useFetchAssetType } from 'hooks/useFetchAssetType';
import { useFetchManufactures } from 'store/manufacture';
import { isRuntimeTriggerType, useFetchTriggerTypes } from 'store/triggerType'; // NEW
import { useFetchTriggerSubTypes } from 'store/triggerSubType'; // NEW
import { useFetchMaintenanceUnits } from 'store/maintenanceUnit'; // NEW
import { useFetchNotificationSettings } from 'store/notificationSettings';
export const SizeOptionParams = 500;

// Form state holds maintenanceTriggerTypeId/triggerSubTypeId/unitId as
// id-strings (Select-bound); the API wants just the numeric ids. Built as an
// explicit object (not `...c`) so the populated relation fields
// (maintenanceTriggerType/triggerSubType/unit) never leak into the payload.
const toPayloadMaintenanceConfigs = (configs: MaintenanceConfig[]) =>
  configs.map((c) => {
    // Runtime's unit comes from the linked sub type's defaultUnit, not a
    // /maintenance-units pick — always send null for it regardless of
    // whatever stray value might be sitting in form state.
    const runtime = isRuntimeTriggerType(c.maintenanceTriggerTypeId);
    return {
      maintenanceTriggerTypeId: c.maintenanceTriggerTypeId
        ? Number(c.maintenanceTriggerTypeId)
        : undefined,
      calculationType: c.calculationType || undefined,
      unitId: runtime ? null : c.unitId ? Number(c.unitId) : undefined,
      triggerSubTypeId: c.triggerSubTypeId ? Number(c.triggerSubTypeId) : undefined,
      frequencyType: c.frequencyType,
      maintenanceFrequency: c.maintenanceFrequency,
      minMaintenanceFrequency: c.minMaintenanceFrequency,
      maxMaintenanceFrequency: c.maxMaintenanceFrequency,
      notificationWindow: c.notificationWindow,
      counterSource: c.counterSource
    };
  });

export const useInjection = ({
  initSearchText,
  refreshDataModels,
  onExport,
  refreshOptions
}: Partial<AssetModelTab>) => {
  const { setFlash } = useFlash();

  const { optionAsset: optionTypes, getOptionAsset, setPageOptionAsset } = useFetchAssetType();
  const handleGetOptionManu = useFetchManufactures();
  const handleGetTriggerTypes = useFetchTriggerTypes(); // NEW
  const handleGetTriggerSubTypes = useFetchTriggerSubTypes(); // NEW
  const handleGetMaintenanceUnits = useFetchMaintenanceUnits(); // NEW
  const handleGetNotificationSettings = useFetchNotificationSettings();

  const [searchText, setSearchText] = useState<string>(initSearchText || '');
  const [showModalAddNew, setShowModalAddNew] = useState<boolean>(false);
  const [showModalEditNew, setShowModalEditNew] = useState<boolean>(false);
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false);

  const [showModalViewPdf, setShoModalViewPdf] = useState<boolean>(false);
  const [modelEdit, setModelEdit] = useState<AssetModel | undefined>(undefined);
  const onCloseAddNew = useCallback(() => {
    setShowModalAddNew(false);
    setPageOptionAsset(1);
  }, [setPageOptionAsset]);

  const onCloseEdit = useCallback(() => {
    setShowModalEditNew(false);
    setPageOptionAsset(1);
  }, [setPageOptionAsset]);

  const listButton = [
    {
      text: 'Export Spreadsheet',
      type: 'primary',
      onClick: () => {
        if (onExport) onExport('xlsx');
      }
    },
    {
      text: 'Export PDF',
      type: 'secondary',
      onClick: () => {
        if (onExport) onExport('pdf');
      }
    },
    {
      text: 'Add New Model',
      type: 'primary',
      onClick: () => setShowModalAddNew(true)
    }
  ];

  const handleAddModels = useCallback(
    async (value: AssetModelForm) => {
      const payload: PayloadAssetModels = {
        manufacturerId: Number(value.manufacture) || undefined,
        manufacturer: value.otherManufacture || '',
        name: value.name,
        typeId: value.type,
        oemAttributes: value?.oemAttribute || [],
        maintenanceEnabled: value?.maintenanceEnabled,
        maintenanceConfigs: value?.maintenanceEnabled // NEW
          ? toPayloadMaintenanceConfigs(value.maintenanceConfigs || [])
          : []
      };
      const data = await createNewAssetModel(payload);
      if (data?.model?.id && value.files?.length) {
        const formData = new FormData();

        value.files.forEach((file: File) => {
          formData.append('files', file);
        });

        await uploadFileAssetModel(data.model.id, formData);
      }
      setFlash({ type: 'success', message: 'Create asset models successfully' });
      refreshDataModels && refreshDataModels();
      onCloseAddNew();
      await Promise.all([
        value.manufacture ? handleGetOptionManu() : Promise.resolve(),
        refreshOptions ? refreshOptions() : Promise.resolve()
      ]);
    },
    [handleGetOptionManu, onCloseAddNew, refreshDataModels, refreshOptions, setFlash]
  );

  const { asyncCallback: asyncAddNewModels } = useAsyncCallback(handleAddModels, []);

  // func edit model
  const handleEditModels = useCallback(
    async (value: AssetModelForm) => {
      if (!modelEdit?.id) return;
      const payload: PayloadAssetModels = {
        manufacturerId: Number(value.manufacture) || undefined,
        manufacturer: value.otherManufacture || '',
        name: value.name,
        typeId: value.type,
        oemAttributes: value?.oemAttribute || [],
        maintenanceEnabled: value?.maintenanceEnabled,
        maintenanceConfigs: value?.maintenanceEnabled // NEW
          ? toPayloadMaintenanceConfigs(value.maintenanceConfigs || [])
          : []
      };
      const data = await updateNewAssetModel(modelEdit.id, payload);
      const fileInitDelete = modelEdit.files.filter((item) => item.deletedAt);
      if (fileInitDelete) {
        await Promise.all(fileInitDelete.map((file) => deleteFileModel(file.storageId)));
      }
      if (data?.model?.id && value.files?.length) {
        const formData = new FormData();

        value.files.forEach((file: File) => {
          formData.append('files', file);
        });

        await uploadFileAssetModel(data.model.id, formData);
      }

      setFlash({ type: 'success', message: 'Update asset models successfully' });
      refreshDataModels && refreshDataModels();
      onCloseEdit();
      await Promise.all([
        value.manufacture ? handleGetOptionManu() : Promise.resolve(),
        refreshOptions ? refreshOptions() : Promise.resolve()
      ]);
    },
    [
      handleGetOptionManu,
      modelEdit?.files,
      modelEdit?.id,
      onCloseEdit,
      refreshDataModels,
      refreshOptions,
      setFlash
    ]
  );

  const { asyncCallback: asyncEditNewModels } = useAsyncCallback(handleEditModels, []);
  // const handleDownloadFile = async (type: string) => {
  //   await downloadFile();
  // };
  // const { asyncCallback: asyncEditNewModels } = useAsyncCallback(handleEditModels, []);

  useEffect(() => {
    const paramsConvert = {
      page: 1,
      size: 999
    };
    getOptionAsset(paramsConvert);
  }, [getOptionAsset]);

  // NEW — fetch trigger types on mount, same pattern as manufacturer
  useEffect(() => {
    handleGetTriggerTypes();
  }, [handleGetTriggerTypes]);

  // NEW — fetch trigger sub types on mount, same pattern as trigger type
  useEffect(() => {
    handleGetTriggerSubTypes();
  }, [handleGetTriggerSubTypes]);

  // NEW — fetch maintenance units on mount, same pattern as trigger type
  useEffect(() => {
    handleGetMaintenanceUnits();
  }, [handleGetMaintenanceUnits]);

  useEffect(() => {
    handleGetNotificationSettings();
  }, [handleGetNotificationSettings]);

  const handleDelete = useCallback(async () => {
    if (!modelEdit?.id) return;
    await deleteAssetModel(Number(modelEdit.id));
    setFlash({ type: 'success', message: 'Delete asset models successfully' });
    setShowModalDelete(false);
    refreshDataModels && refreshDataModels();
    refreshOptions && (await refreshOptions());
  }, [modelEdit, setFlash, refreshDataModels, refreshOptions]);

  const { asyncCallback: asyncDeleter } = useAsyncCallback(handleDelete, []);

  const handleDeleteCreatedFile = useCallback(
    (idFile: number) => {
      if (!modelEdit) return;
      const newEdit: AssetModel = {
        ...modelEdit,
        files: modelEdit.files.map((item) => {
          if (item.id === idFile) {
            return {
              ...item,
              deletedAt: new Date().toString()
            };
          }
          return item;
        })
      };
      setModelEdit(newEdit);
    },
    [modelEdit]
  );

  useEffect(() => {
    handleGetOptionManu();
  }, [handleGetOptionManu]);

  return {
    searchText,
    setSearchText,
    listButton,
    showModalAddNew,
    onCloseAddNew,
    asyncAddNewModels,
    asyncEditNewModels,
    showModalViewPdf,
    setShoModalViewPdf,
    modelEdit,
    setModelEdit,
    showModalEditNew,
    onCloseEdit,
    handleDeleteCreatedFile,
    showModalDelete,
    setShowModalDelete,
    asyncDeleter,
    optionTypes,
    setShowModalEditNew
  };
};
